<template>
  <div class="card mb-25 border-0 rounded-0 bg-white">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30">
      <!-- Section des filtres -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card border">
            <div class="card-header bg-light">
              <h5 class="mb-0">
                <i class="flaticon-filter me-2"></i>Filtres de recherche
              </h5>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <!-- Recherche par mot-clé -->
                <div class="col-md-3 col-sm-6">
                  <label class="form-label fw-medium">Mot-clé</label>
                  <input v-model="filtres.motCle" type="text" class="form-control" placeholder="Référence, emplacement, usager..." />
                </div>

                <!-- Filtre par emplacement -->
                <div class="col-md-3 col-sm-6">
                  <label class="form-label fw-medium">Code emplacement</label>
                  <input v-model="filtres.codeemp" type="text" class="form-control" placeholder="Code emplacement" />
                </div>

                <!-- Filtre par usager -->
                <div class="col-md-3 col-sm-6">
                  <label class="form-label fw-medium">Matricule usager</label>
                  <input v-model="filtres.matricule" type="text" class="form-control" placeholder="Matricule" />
                </div>

                <!-- Filtre par étape de validation -->
                <div class="col-md-3 col-sm-6">
                  <label class="form-label fw-medium">Étape validation</label>
                  <select v-model="filtres.etape_validation" class="form-select">
                    <option value="">Tous</option>
                    <option value="true">Validé</option>
                    <option value="false">Non validé</option>
                  </select>
                </div>

                <!-- Filtre par résiliation -->
                <div class="col-md-3 col-sm-6">
                  <label class="form-label fw-medium">Statut</label>
                  <select v-model="filtres.resilie" class="form-select">
                    <option value="">Tous</option>
                    <option value="false">Actifs</option>
                    <option value="true">Résiliés</option>
                  </select>
                </div>

                <!-- Date début -->
                <div class="col-md-3 col-sm-6">
                  <label class="form-label fw-medium">Date début</label>
                  <input v-model="filtres.dateDebut" type="date" class="form-control" />
                </div>

                <!-- Date fin -->
                <div class="col-md-3 col-sm-6">
                  <label class="form-label fw-medium">Date fin</label>
                  <input v-model="filtres.dateFin" type="date" class="form-control" />
                </div>

                <!-- Boutons d'action -->
                <div class="col-md-3 col-sm-6 d-flex align-items-end">
                  <button @click="rechercher" class="btn btn-primary me-2" :disabled="loading">
                    <i class="flaticon-search me-1"></i>Rechercher
                  </button>
                  <button @click="reinitialiser" class="btn btn-secondary">
                    <i class="flaticon-refresh me-1"></i>Réinitialiser
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bouton d'ajout -->
      <div class="row mb-3">
        <div class="col-12">
          <router-link v-if="checkPrivilege('AddContrat')" to="/ajouter-contrat" class="btn btn-success">
            <i class="flaticon-plus me-2"></i>Créer un contrat
          </router-link>
        </div>
      </div>

      <!-- Tableau des contrats -->
      <div class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th>Référence</th>
              <th>Emplacement</th>
              <th>Usager</th>
              <th>Loyer (FCFA)</th>
              <th>Solde initial</th>
              <th>Date création</th>
              <th>Date signature</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="9" class="text-center">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Chargement...</span>
                </div>
              </td>
            </tr>
            <tr v-else-if="contrats.length === 0">
              <td colspan="9" class="text-center text-muted">Aucun contrat trouvé</td>
            </tr>
            <tr v-else v-for="contrat in contrats" :key="contrat.id">
              <td>{{ contrat.reference || 'N/A' }}</td>
              <td>
                <span v-if="contrat.codeemp">
                  {{ contrat.codeemp.ncode_emp || contrat.codeemp.codeemp }}
                </span>
                <span v-else>N/A</span>
              </td>
              <td>
                <span v-if="contrat.matricule">
                  {{ contrat.matricule.nom }} {{ contrat.matricule.prenom }}
                  <br>
                  <small class="text-muted">{{ contrat.matricule.matricule }}</small>
                </span>
                <span v-else>{{ contrat.locataire || 'N/A' }}</span>
              </td>
              <td>{{ formatNumber(contrat.loyer) }}</td>
              <td>
                <span :class="contrat.type_solde === 'Avoir' ? 'text-success' : 'text-danger'">
                  {{ formatNumber(contrat.solde_initial) }}
                </span>
                <br>
                <small class="text-muted">({{ contrat.type_solde }})</small>
              </td>
              <td>{{ formatDate(contrat.date_creation) }}</td>
              <td>{{ formatDate(contrat.date_signature) }}</td>
              <td>
                <span v-if="contrat.date_resiliation" class="badge bg-danger">Résilié</span>
                <span v-else-if="contrat.etape_validation" class="badge bg-success">Actif</span>
                <span v-else class="badge bg-warning">En attente</span>
              </td>
              <td>
                <div class="dropdown">
                  <button class="btn btn-sm btn-primary dropdown-toggle" type="button" 
                    id="dropdownActions" data-bs-toggle="dropdown" aria-expanded="false">
                    <i class="flaticon-menu"></i> Actions
                  </button>
                  <ul class="dropdown-menu" aria-labelledby="dropdownActions">
                    <li v-if="checkPrivilege('ViewContrat')">
                      <a class="dropdown-item" href="#" @click.prevent="voirDetails(contrat.id)">
                        <i class="flaticon-eye me-2"></i>Détail
                      </a>
                    </li>
                    <li v-if="checkPrivilege('EditContrat')">
                      <a class="dropdown-item text-success" href="#" @click.prevent="activerContrat(contrat.id)">
                        <i class="flaticon-check me-2"></i>Activer
                      </a>
                    </li>
                    <li v-if="checkPrivilege('EditContrat')">
                      <a class="dropdown-item text-warning" @click.prevent="desactiverContrat(contrat.id)">
                        <i class="flaticon-close me-2"></i>Désactiver
                      </a>
                    </li>
                    <li v-if="checkPrivilege('EditContrat')">
                      <a class="dropdown-item text-danger" @click.prevent="resilierContrat(contrat.id)">
                        <i class="flaticon-cancel me-2"></i>Résiliation
                      </a>
                    </li>
                    <li v-if="checkPrivilege('EditContrat')">
                      <a class="dropdown-item text-info" @click.prevent="muterContrat(contrat.id)">
                        <i class="flaticon-exchange me-2"></i>Mutation
                      </a>
                    </li>
                    <li v-if="checkPrivilege('DeleteContrat')">
                      <a class="dropdown-item text-danger" @click.prevent="supprimerContrat(contrat.id)">
                        <i class="flaticon-trash me-2"></i>Supprimer
                      </a>
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="d-flex justify-content-between align-items-center mt-3">
        <div>
          <span class="text-muted">
            Page {{ currentPage }} sur {{ totalPages }} ({{ totalElements }} contrats)
          </span>
        </div>
        <nav>
          <ul class="pagination mb-0">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <button class="page-link" @click="changerPage(currentPage - 1)" :disabled="currentPage === 1">
                Précédent
              </button>
            </li>
            <li v-for="page in pagesAffichees" :key="page" class="page-item" :class="{ active: page === currentPage }">
              <button class="page-link" @click="changerPage(page)">{{ page }}</button>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
              <button class="page-link" @click="changerPage(currentPage + 1)" :disabled="currentPage === totalPages">
                Suivant
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import ApiService from "@/services/ApiService";
import { checkPrivilege } from "@/utils/utils";
import Swal from "sweetalert2";

export default defineComponent({
  name: "ListeContrat",
  setup() {
    const router = useRouter();
    const loading = ref(false);
    const contrats = ref<any[]>([]);
    const currentPage = ref(1);
    const totalPages = ref(1);
    const totalElements = ref(0);
    const limit = ref(10);

    const filtres = ref({
      motCle: "",
      codeemp: "",
      matricule: "",
      etape_validation: "",
      resilie: "",
      dateDebut: "",
      dateFin: "",
    });

    const pagesAffichees = computed(() => {
      const pages: number[] = [];
      const maxPages = 5;
      let start = Math.max(1, currentPage.value - Math.floor(maxPages / 2));
      let end = Math.min(totalPages.value, start + maxPages - 1);
      if (end - start < maxPages - 1) {
        start = Math.max(1, end - maxPages + 1);
      }
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    });

    const chargerContrats = async () => {
      loading.value = true;
      try {
        const params = {
          page: currentPage.value,
          limit: limit.value,
          ...filtres.value,
        };

        // Nettoyer les paramètres vides
        Object.keys(params).forEach(key => {
          if (params[key] === "" || params[key] === null || params[key] === undefined) {
            delete params[key];
          }
        });

        const response = await ApiService.post("/contrats/liste", params);
        console.log("Les contrats sont récupérés avec succès.");
        console.log(response.data);

        if (response.data.code === 200) {
          contrats.value = response.data.data.data || [];
          totalPages.value = response.data.data.totalPages || 1;
          totalElements.value = response.data.data.totalElements || 0;
          currentPage.value = response.data.data.currentPage || 1;
        } 
      } catch (error: any) {
        console.error("Erreur lors du chargement des contrats:", error);
        contrats.value = [];
      } finally {
        loading.value = false;
      }
    };

    const rechercher = () => {
      currentPage.value = 1;
      chargerContrats();
    };

    const reinitialiser = () => {
      filtres.value = {
        motCle: "",
        codeemp: "",
        matricule: "",
        etape_validation: "",
        resilie: "",
        dateDebut: "",
        dateFin: "",
      };
      currentPage.value = 1;
      chargerContrats();
    };

    const changerPage = (page: number) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        chargerContrats();
      }
    };

    const voirDetails = (id: number) => {
      router.push(`/detail-contrat/${id}`);
    };

    const editerContrat = (id: number) => {
      router.push(`/modifier-contrat/${id}`);
    };

    const activerContrat = async (id: number) => {
      const result = await Swal.fire({
        title: "Activer le contrat",
        text: "Êtes-vous sûr de vouloir activer ce contrat ?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Oui, activer",
        cancelButtonText: "Annuler",
        confirmButtonColor: "#28a745",
      });

      if (result.isConfirmed) {
        try {
          const response = await ApiService.put(`/contrats/${id}/activer`, {});
          if (response.data && response.data.success) {
            await Swal.fire({
              icon: "success",
              title: "Succès",
              text: response.data.message || "Le contrat a été activé avec succès.",
            });
            chargerContrats();
          }
        } catch (error: any) {
          await Swal.fire({
            icon: "error",
            title: "Erreur",
            text: error.response?.data?.message || "Une erreur est survenue lors de l'activation.",
          });
        }
      }
    };

    const desactiverContrat = async (id: number) => {
      const result = await Swal.fire({
        title: "Désactiver le contrat",
        text: "Êtes-vous sûr de vouloir désactiver ce contrat ?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Oui, désactiver",
        cancelButtonText: "Annuler",
        confirmButtonColor: "#ffc107",
      });

      if (result.isConfirmed) {
        try {
          const response = await ApiService.put(`/contrats/${id}/desactiver`, {});
          if (response.data && response.data.success) {
            await Swal.fire({
              icon: "success",
              title: "Succès",
              text: response.data.message || "Le contrat a été désactivé avec succès.",
            });
            chargerContrats();
          }
        } catch (error: any) {
          await Swal.fire({
            icon: "error",
            title: "Erreur",
            text: error.response?.data?.message || "Une erreur est survenue lors de la désactivation.",
          });
        }
      }
    };

    const resilierContrat = async (id: number) => {
      const { value: motif } = await Swal.fire({
        title: "Résilier le contrat",
        text: "Veuillez indiquer le motif de résiliation",
        input: "textarea",
        inputLabel: "Motif de résiliation",
        inputPlaceholder: "Entrez le motif de résiliation...",
        inputAttributes: {
          "aria-label": "Motif de résiliation",
        },
        showCancelButton: true,
        confirmButtonText: "Résilier",
        cancelButtonText: "Annuler",
        confirmButtonColor: "#dc3545",
        inputValidator: (value) => {
          if (!value) {
            return "Le motif de résiliation est obligatoire";
          }
        },
      });

      if (motif) {
        try {
          const response = await ApiService.put(`/contrats/${id}/resilier`, {
            motif_resiliation: motif,
          });
          if (response.data && response.data.success) {
            await Swal.fire({
              icon: "success",
              title: "Succès",
              text: response.data.message || "Le contrat a été résilié avec succès.",
            });
            chargerContrats();
          }
        } catch (error: any) {
          await Swal.fire({
            icon: "error",
            title: "Erreur",
            text: error.response?.data?.message || "Une erreur est survenue lors de la résiliation.",
          });
        }
      }
    };

    const muterContrat = (id: number) => {
      router.push(`/mutation-contrat/${id}`);
    };

    const supprimerContrat = async (id: number) => {
      const result = await Swal.fire({
        title: "Supprimer le contrat",
        text: "Cette action est irréversible. Êtes-vous sûr de vouloir supprimer ce contrat ?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Oui, supprimer",
        cancelButtonText: "Annuler",
        confirmButtonColor: "#dc3545",
      });

      if (result.isConfirmed) {
        try {
          const response = await ApiService.delete(`/contrats/${id}`);
          if (response.data && response.data.success) {
            await Swal.fire({
              icon: "success",
              title: "Succès",
              text: response.data.message || "Le contrat a été supprimé avec succès.",
            });
            chargerContrats();
          }
        } catch (error: any) {
          await Swal.fire({
            icon: "error",
            title: "Erreur",
            text: error.response?.data?.message || "Une erreur est survenue lors de la suppression.",
          });
        }
      }
    };

    const historiserContrat = async (id: number) => {
      const { value: raison } = await Swal.fire({
        title: "Historiser le contrat",
        text: "Veuillez indiquer la raison de l'historisation",
        input: "text",
        inputLabel: "Raison",
        inputPlaceholder: "Raison de l'historisation...",
        showCancelButton: true,
        confirmButtonText: "Historiser",
        cancelButtonText: "Annuler",
        confirmButtonColor: "#6c757d",
        inputValidator: (value) => {
          if (!value) {
            return "La raison est obligatoire";
          }
        },
      });

      if (raison) {
        try {
          const response = await ApiService.post(`/contrats/${id}/historiser`, {
            raison: raison,
          });
          if (response.data && response.data.success) {
            await Swal.fire({
              icon: "success",
              title: "Succès",
              text: response.data.message || "Le contrat a été historisé avec succès.",
            });
            chargerContrats();
          }
        } catch (error: any) {
          await Swal.fire({
            icon: "error",
            title: "Erreur",
            text: error.response?.data?.message || "Une erreur est survenue lors de l'historisation.",
          });
        }
      }
    };

    const formatNumber = (value: number) => {
      if (!value) return "0";
      return new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    };

    const formatDate = (date: string | Date) => {
      if (!date) return "N/A";
      const d = new Date(date);
      return d.toLocaleDateString("fr-FR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    };

    onMounted(() => {
      chargerContrats();
    });

    return {
      loading,
      contrats,
      currentPage,
      totalPages,
      totalElements,
      limit,
      filtres,
      pagesAffichees,
      rechercher,
      reinitialiser,
      changerPage,
      voirDetails,
      editerContrat,
      activerContrat,
      desactiverContrat,
      resilierContrat,
      muterContrat,
      supprimerContrat,
      historiserContrat,
      formatNumber,
      formatDate,
      checkPrivilege,
    };
  },
});
</script>

<style scoped>
.table th {
  background-color: #f8f9fa;
  font-weight: 600;
}
</style>

